import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import { PaymentSystem } from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { TAnswers } from 'models/common.model'
import { getSelectedAnswers } from 'nove8/helpers/getSelectedAnswers'
import { NONE } from 'root-constants'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'
import { NOTHING_ANSWER } from './pages/questions/constants'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const getAnswers = (): Partial<TAnswers> | null =>
  useZustandSessionStore.getState().userAnswers

const shouldSkipOnePageOnFeelingsAnswer = () => {
  const savedAnswers = getAnswers()
  const feelingsAnswer = getSelectedAnswers(
    savedAnswers,
    PageId.FEELINGS_PN,
  ) as string[]

  if (feelingsAnswer.every((item) => item === NOTHING_ANSWER)) {
    return true
  }

  return false
}

const isBreedIdScreenAvailable = () => {
  const config = useZustandGlobalStore.getState().firebaseRemoteConfig
  return config ? config.getBoolean('breed_id_setup_pn') : false
}

const shouldSkipPagesOnChallengesAnswer = () => {
  const savedAnswers = getAnswers()
  const challengesAnswer = getSelectedAnswers(
    savedAnswers,
    PageId.CHALLENGES_PN,
  ) as string[]

  if (
    challengesAnswer.length === 0 ||
    challengesAnswer.every((item) => item === NONE)
  ) {
    return true
  }

  return false
}

const shouldSkipOnePageOnBarkingAnswer = () => {
  const savedAnswers = getAnswers()
  const challengesAnswer = getSelectedAnswers(
    savedAnswers,
    PageId.BARKING_PN,
  ) as string[]

  if (
    challengesAnswer.length === 0 ||
    challengesAnswer.every((item) => item === NONE)
  ) {
    return true
  }

  return false
}

export const getXStateConfigWoofzPn = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    // Pay attention to the order of the conditions!
    always: [
      // Cover case when page was reloaded or connection was lost
      // during the purchase polling
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const url = new URL(window.location.href)
          const pageId = url.pathname.split('/').pop()
          const isNewsletterPage = pageId === PageId.NEWSLETTER_URGE_PN

          return !hasEmail && isNewsletterPage
        },
        target: pages.find((p) => p.pageId.includes('email'))?.pageId,
      },
      // Do not redirect users to the next breakpoint before email page
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          return !hasEmail && !hasSubscription && !hasAccount && hasAnswers
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_PN,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_NOVE8,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_PN,
      },
      {
        target: pages[0].pageId, // Always transition to first page as fallback
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.FEELINGS_PN && {
            NEXT_FLOW_STEP: [
              {
                cond: shouldSkipOnePageOnFeelingsAnswer,
                target: PageId.REACTION_PN,
              },
              {
                target: PageId.URGE_2_PN,
              },
            ],
          }),
          ...(page.pageId === PageId.CHALLENGES_PN && {
            NEXT_FLOW_STEP: [
              {
                cond: shouldSkipPagesOnChallengesAnswer,
                target: PageId.BARKING_PN,
              },
              {
                target: PageId.FEELINGS_PN,
              },
            ],
            PREVIOUS_FLOW_STEP: [
              {
                cond: isBreedIdScreenAvailable,
                target: PageId.DOG_BREED_PN,
              },
              {
                target: PageId.DOG_BREED_PN,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_BREED_PN && {
            NEXT_FLOW_STEP: [
              {
                cond: isBreedIdScreenAvailable,
                target: PageId.DOG_BREED_ID_PN,
              },
              {
                target: PageId.CHALLENGES_PN,
              },
            ],
          }),
          ...(page.pageId === PageId.BARKING_PN && {
            NEXT_FLOW_STEP: [
              {
                cond: shouldSkipOnePageOnBarkingAnswer,
                target: PageId.IMPROVE_PN,
              },
              {
                target: PageId.URGE_3_PN,
              },
            ],
            PREVIOUS_FLOW_STEP: [
              {
                cond: shouldSkipPagesOnChallengesAnswer,
                target: PageId.CHALLENGES_PN,
              },
              {
                target: PageId.YELLING_MOMENTS_PN,
              },
            ],
          }),
          ...(page.pageId === PageId.IMPROVE_PN && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: shouldSkipOnePageOnBarkingAnswer,
                target: PageId.BARKING_PN,
              },
              {
                target: PageId.URGE_3_PN,
              },
            ],
          }),
          ...(page.pageId === PageId.REACTION_PN && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: shouldSkipOnePageOnFeelingsAnswer,
                target: PageId.FEELINGS_PN,
              },
              {
                target: PageId.URGE_2_PN,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_STATISTICS_PN && {
            PREVIOUS_FLOW_STEP: {
              target: PageId.FREQUENCY_PN,
            },
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_PN && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_PN,
              },
            ],
          }),
        },
      },
    }),
    {},
  ),
  [PageId.DOWNLOAD_PN]: {
    type: 'final',
  },
})
